import { Subscribable } from 'knockout';
import { Person } from 'models/person';
import { Topic } from 'models/topic';

export default class TextThreadEditorContentManager {
    private allowedMediaTypes = ko.pureComputed(() => this.getAllowedMediaTypes());    

    textIsAllowed = ko.pureComputed(() => this.checkIfTextIsAllowed())
    imagesAreAllowed = ko.pureComputed(() => this.allowedMediaTypes().includes(enums.ThreadMediaTypes.Images.value));
    filesAreAllowed = ko.pureComputed(() => this.allowedMediaTypes().includes(enums.ThreadMediaTypes.Files.value));
    videoIsAllowed = ko.pureComputed(() => this.allowedMediaTypes().includes(enums.ThreadMediaTypes.Video.value));
    audioIsAllowed = ko.pureComputed(() => this.allowedMediaTypes().includes(enums.ThreadMediaTypes.Audio.value));

    mediaIsRequired = ko.pureComputed(() => this.checkIfMediaIsRequired());

    constructor(
        private topic: Subscribable<Topic | undefined>,
        private user: Subscribable<Person | undefined>,
        private threadOwner: Subscribable<Person | undefined>,
        private isReply: Subscribable<boolean>,
        private forcedTextMode: Subscribable<boolean>
    ) { }

    private checkIfTextIsAllowed() {
        var topic = this.topic(),
            threadOwner = this.threadOwner();

        if (topic != null && threadOwner != null) {
            if (!threadOwner.isRegular() || this.forcedTextMode())
                return true;

            if (topic.isMediaOnly() || topic.isDocumentsCollection())
                return this.isReply();

            if (topic.isVoxPop())
                return this.isReply() && topic.participantsReplyType() !== enums.ParticipantsReplyTypes.OnlyVideo.value;

            return true;
        }

        return false;
    }

    private shouldApplyMediaRulesToComments(topic: Topic) {
        switch (topic.topicContentType()) {
            case enums.TopicContentTypes.TextComments.value:
            case enums.TopicContentTypes.MultipleComments.value:
            case enums.TopicContentTypes.MediaOnly.value:
            case enums.TopicContentTypes.CollectionOfDocuments.value:
            case enums.TopicContentTypes.Ideation.value:
                return true;

            default:
                return false;
        }
    }

    private shouldApplyMediaRulesToReplies(topic: Topic) {
        switch (topic.topicContentType()) {
            case enums.TopicContentTypes.TextComments.value:
            case enums.TopicContentTypes.MultipleComments.value:
            case enums.TopicContentTypes.Survey.value:
            case enums.TopicContentTypes.MediaOnly.value:
            case enums.TopicContentTypes.CollectionOfDocuments.value:
            case enums.TopicContentTypes.VirtualPinBoard.value:
            case enums.TopicContentTypes.HeatMap.value:
                return true;

            case enums.TopicContentTypes.VideoPresentation.value:
                return false; //true if presentationType === WCC.Enums.VideoPresentationTypes.PresentationAndSurvey.value

            default:
                return false;
        }
    }

    private checkIfMediaIsRequired() {
        const topic = this.topic();
        const person = this.threadOwner();

        if (topic != undefined && person != undefined) {
            if (!person.isRegular())
                return false;

            if (topic.isVoxPop())
                return this.checkIfMediaIsRequiredForVoxPop(topic);

            if (!topic.isThreadMediaMandatory())
                return false;

            return !this.isReply() && !(topic.isSurvey() || topic.isPinBoard());
        }

        return false;
    }

    private checkIfMediaIsRequiredForVoxPop(topic: Topic) {
        if (!this.isReply())
            return topic.participantsCommentType() == enums.ParticipantsCommentTypes.OnlyVideo.value || !this.forcedTextMode();

        return false;
    }

    private getAllowedMediaTypes() {
        const topic = this.topic();
        const threadOwner = this.threadOwner();
        const user = this.user();

        if (topic != undefined && threadOwner != undefined && user != undefined) {
            if (!threadOwner.isRegular())
                return topic.parsedAllowedMediaTypes();

            if (topic.isVoxPop())
                return this.getAllowedMediaTypesForVoxPop(topic) ?? [];

            if (topic.isScreenRecording() && !user.isRegular() && threadOwner.isRegular())
                return [enums.ThreadMediaTypes.Video.value];

            var apply = !this.isReply() && this.shouldApplyMediaRulesToComments(topic) ||
                this.isReply() && this.shouldApplyMediaRulesToReplies(topic);

            if (apply)
                return topic.parsedAllowedMediaTypes();
        }

        return [];
    }

    private getAllowedMediaTypesForVoxPop(topic: Topic) {
        if (!this.isReply()) {
            return this.forcedTextMode() ? [] : [enums.ThreadMediaTypes.Video.value];
        } else {
            return topic.participantsReplyType() === enums.ParticipantsReplyTypes.OnlyVideo.value ?
                [enums.ThreadMediaTypes.Video.value] :
                topic.parsedAllowedMediaTypes();
        }
    }
}